import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
} from "react";
import type { ServiceBookingSelection } from "../components/serviceBooking";
import type { Service } from "../../../hooks/useServices";
import type { EventPassChoice } from "../eventPass";

interface MainGuest {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  idType?: Array<{ label: string; id: number }>;
  idNumber?: string;
  // Document scan fields — optional for retro-compatibility
  idnum?: string | null;
  nationality?: string | null;
  birthdate?: string | null;
  photo_url?: string | null;
  // SAM-253: how the main guest's ID document was captured — 'manual' (host
  // captured it here, scanned or typed) or 'share_link' (guest completes their
  // own ID document later via the SMS link the backend sends automatically).
  captureMode?: 'manual' | 'share_link';
  // SAM-198 «Existing client»: the app user picked in Client booking. Set only for a service
  // booking, whose on_behalf_of then carries {user_id} instead of a new main_guest.
  userId?: number | string | null;
}

interface Companion {
  firstName: string;
  lastName: string;
  // SAM-842: what the guests step captured for this companion (scan fields null when typed).
  tel?: string;
  idnum?: string | null;
  nationality?: string | null;
  birthdate?: string | null;
  photo_url?: string | null;
}

interface Location {
  estate_group_id: Array<{ label: string; id: number }>;
  estate_id: Array<{ label: string; id: number }>;
}

interface Invitation {
  // SAM-199 second pass — these three used to be local useState in AccessForm /
  // ServiceBookingSelector, which meant they reset every time AccessForm
  // unmounted (e.g. the operator clicks "Dates" on the progress bar to come
  // back and check something). `duration` survived because it already lived
  // here; these did not, forcing a full re-pick. Lifted here so "Back" never
  // wipes a choice the operator already made.
  accessKind?: 'residential' | 'business' | 'event';
  bookingKind?: 'product' | 'service';
  // The full Service object for the SERVICE path (not just id/name, unlike
  // `serviceBooking` below) — needed to re-render ServiceSelectControl's
  // dropdown and the schedule/quantities step's booking_options on remount.
  selectedService?: Service | null;
  // SAM-1304 — the event of an Event pass (New pass > Event); its business is the pass's.
  selectedEvent?: EventPassChoice | null;
  selectedServiceTime?: string | null;
  // Manual-fallback quantities (ServiceQuantitySelector's 'manual' shape only) — lifted
  // for the same remount-survival reason as selectedService/selectedServiceTime.
  manualServiceQuantities?: Record<string, number>;
  // SAM-590 — add-on quantities for the SERVICE path, lifted for the same remount-survival
  // reason as manualServiceQuantities. Keyed by add-on id; only non-zero entries are actually
  // sent (see buildServiceBookingSelection).
  serviceAddOnQuantities?: Record<string, number>;
  // SAM-624 — the operator's rate-group pick per booking option (optionId -> group id) and the
  // cabana/unit pick (resource key), lifted for the same remount-survival reason. Absent entries
  // fall back to the default group / no resource.
  serviceRateGroups?: Record<string, string>;
  serviceResourceKey?: string | null;
  accessType?: Array<{ label: string; id: number }>;
  duration?: {
    mode?: "one" | "multiple";
    date?: string;
    startDate?: string;
    endDate?: string;
    // SAM-199 follow-up — carried through from HowLongControl's DurationOption
    // so the SERVICE booking path can read the SAME guest count instead of
    // asking again (see AccessForm's serviceAdults/serviceChildren). The
    // canonical place this also lands is `guests.companions`, set at the same
    // time by handleHowLongComplete.
    adults?: number;
    children?: number;
  };
  location?: Location;
  guests?: {
    mainGuest?: MainGuest;
    companions?: {
      adults: number;
      childrens: number;
      companions?: Companion[];
    };
  };
  // SAM-806 — each guest's scanned ID (keyed like DocumentScanForm, `${firstName}-${lastName}`) and how each guest
  // was captured, lifted for the same remount-survival reason: going Back to the guests step shows the scan as it
  // was and Next saves it as a scan, not as typed details.
  guestScans?: Record<string, { file: File; preview: string; documentData?: unknown }>;
  guestInputModes?: Record<string, 'scan' | 'manual'>;
  legacy_code?: string;
  code?: string;
  business_name?: string;
  product?: Array<{ label: string; id: number; price?: number; payment_gateway?: 'carnet' | 'portal' }>;
  // SAM-199 — front-desk SERVICE booking (alternative to `product`, mutually
  // exclusive in the UI). Shape defined by buildServiceBookingSelection in
  // src/pages/invitation/components/serviceBooking.ts. Never carries a price —
  // the server recomputes the total from `quantities` on submit. Read by
  // AddInvitation.tsx's handleConfirm, which posts it (via
  // buildOrchestratePayload in serviceBookingOrchestration.ts) to
  // POST /bookings/payment/orchestrate with an `on_behalf_of` built from
  // guests/companions instead of the product_id/credit_card path.
  serviceBooking?: ServiceBookingSelection | null;
  // SAM-199 — idempotency key for the CURRENT service-booking submit attempt.
  // Generated once (handleConfirm, via getOrCreateIdempotencyKey) and REUSED on
  // every retry of that same attempt so a retry after a network blip / 3DS
  // failure never double-books or double-charges. Cleared whenever the
  // underlying `serviceBooking` selection changes (a materially different
  // request needs a new key) and reset with the rest of the drawer's state on
  // open/close.
  serviceBookingIdempotencyKey?: string | null;
  creditCard?: {
    cardNumber: string;
    cardHolder: string;
    expiryDate: string; // Format: MM/YY
    cvv: string;
  };
  threeDSData?: {
    transactionId?: string;
    authenticationTransactionId?: string;
    referenceId?: string;
    status?: string;
  };
}

interface InvitationContextProps {
  invitation: Partial<Invitation>;
  updateInvitation: (invitation: Partial<Invitation>) => void;
  getCreateInvitationPayload: () => any;
  resetInvitation: () => void;
}

const InvitationContext = createContext<InvitationContextProps | undefined>(undefined);

export const InvitationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [invitation, setInvitation] = useState<Partial<Invitation>>({});

  const updateInvitation = useCallback((newInvitation: Partial<Invitation>) => {
    setInvitation((prev) => {
      const updated = {
        ...prev,
        ...newInvitation,
      };
      
      // Debug logs for location updates
      if (newInvitation.location) {
      }
      
      return updated;
    });
  }, []);

  const resetInvitation = useCallback(() => {
    setInvitation({});
  }, []);

  const getCreateInvitationPayload = useCallback(() => {
    return invitation;
  }, [invitation]);

  return (
    <InvitationContext.Provider
      value={{
        invitation,
        updateInvitation,
        getCreateInvitationPayload,
        resetInvitation,
      }}
    >
      {children}
    </InvitationContext.Provider>
  );
};

export const useInvitationContext = () => {
  const context = useContext(InvitationContext);
  if (!context) {
    throw new Error("useInvitationContext must be used within an InvitationProvider");
  }
  return context;
};
